import { getBaseStep } from './OnboardingSteps'
import { type OnboardingInfo, type Step } from './types'

const getFullContextPayload = (userId: string, step: Step, data: OnboardingInfo, stepMetadata?: Record<string, any>) => ({
  // Basic tracking
  referrer_page: document.referrer,
  source: new URLSearchParams(window.location.search).get('source'),
  userId,
  step,
  ...stepMetadata,

  // Enrollment event data
  enrollment_event_id: data.enrollment_event.id,
  member_id: data.member?.id,
  member_state: data.member?.state,
  enrollment_event_status: data.enrollment_event.status,
  progress: data.progress,
  enrollment_event_type: data.enrollment_event.type,
  enrollment_event_subtype: data.enrollment_event.subtype,

  // Allowance data
  allowance_amount: data.allowance?.allowance_amount?.formatted_amount,
  qme_treatment: data.allowance?.qme_treatment,

  // Enrollment status
  medical_plan_selected: data.enrollments?.some(e => e.covers_medical) || false,
  dental_plan_selected: data.enrollments?.some(e => e.covers_dental) || false,
  vision_plan_selected: data.enrollments?.some(e => e.covers_vision) || false,
  total_enrollments: data.enrollments?.length || 0,

  // Missing fields
  missing_allowance_fields: data.missing_fields.allowance || [],
  missing_plans_selection_fields: data.missing_fields.plans_selection || [],
  missing_enrollment_fields: data.missing_fields.enrollment || [],

  // Additional participants
  additional_participants_count: data.additional_participants?.length || 0,
  has_spouse: data.additional_participants?.some(p => p.relationship === 'spouse') || false,
  has_children: data.additional_participants?.some(p => p.relationship === 'child') || false,

  // Medicare eligibility
  has_medicare_eligible: data.restrictions?.medicare_eligible && data.restrictions.medicare_eligible.length > 0,
  medicare_eligible_members: data.restrictions?.medicare_eligible || [],

  // Enrollment categories (e.g., "general_medical", "off_thatch_medical", "medicare", "guardian_dental", etc.)
  medical_enrollment_category: data.enrollments?.find(e => e.covers_medical)?.category || null,
  dental_enrollment_category: data.enrollments?.find(e => e.covers_dental)?.category || null,
  vision_enrollment_category: data.enrollments?.find(e => e.covers_vision)?.category || null,

  // Plan selection metadata (TODO) and restrictions
  ...data.restrictions,
})

const emitGenerateAllowancesPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Personal details', getFullContextPayload(userId, 'generate_allowances', data))

const emitPhoneVerificationPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Phone verification', getFullContextPayload(userId, 'phone_verification', data))

const emitViewAllowancesPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Your budget', getFullContextPayload(userId, 'view_allowances', data))

const emitAdditionalDetailsPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Additional details', getFullContextPayload(userId, 'additional_details', data))

const emitSubsidyEligiblePageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Subsidy eligible', getFullContextPayload(userId, 'subsidy_eligible', data))

const emitMedicalPlansPageViewEvent = (userId: string, data: OnboardingInfo) => {
  window.segment?.page('[EE] Medical plans', {
    ...getFullContextPayload(userId, 'medical_plan', data),
    recommended_plan_ids: data.recommended_medical_plans?.map(plan => plan.plan_id),
    recommended_plans_count: data.recommended_medical_plans?.length || 0,
    provider_preferences_count: data.provider_preferences?.length || 0,
  })
}

const emitMedicalPlanDetailsPageViewEvent = (userId: string, data: OnboardingInfo, stepMetadata?: Record<string, any>) =>
  window.segment?.page('[EE] Medical plan details', {
    ...getFullContextPayload(userId, 'medical_plan_details', data, stepMetadata),
    plan_id: stepMetadata?.planIds[0],
  })

const emitMedicalPlanComparisonPageViewEvent = (userId: string, data: OnboardingInfo, stepMetadata?: Record<string, any>) =>
  window.segment?.page('[EE] Medical plan comparison', {
    ...getFullContextPayload(userId, 'medical_plan_comparison', data, stepMetadata),
    plan_ids: stepMetadata?.planIds,
    plans_count: stepMetadata?.planIds?.length || 0,
  })

const emitInNetworkVerificationPageViewEvent = (userId: string, data: OnboardingInfo, stepMetadata?: Record<string, any>) =>
  window.segment?.page('[EE] Medical plan comparison', {
    ...getFullContextPayload(userId, 'medical_plan_comparison', data, stepMetadata),
    plan_ids: stepMetadata?.planIds,
    plans_count: stepMetadata?.planIds?.length || 0,
  })

const emitDentalPlansPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Dental plans', getFullContextPayload(userId, 'dental_plan', data))

const emitVisionPlansPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Vision plans', getFullContextPayload(userId, 'vision_plan', data))

const emitStateFormPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] State form', {
    ...getFullContextPayload(userId, 'state_form', data),
    state_config: data.state_config,
  })

const emitFinalizeEnrollmentPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Finalize enrollment', getFullContextPayload(userId, 'finalize_enrollment', data))

const emitOptedOutPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Opted out', {
    ...getFullContextPayload(userId, 'opted_out', data),
    decline_reason: data.decline_reason,
  })

const emitOptedOutFromSubsidyEligiblePageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Opted out from subsidy eligible', {
    ...getFullContextPayload(userId, 'opted_out_from_subsidy_eligible', data),
    decline_reason: data.decline_reason,
  })

const emitRenewalConfirmInfoPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Renewal confirm info', getFullContextPayload(userId, 'renewal_confirm_info', data))

const emitRenewalShowSummaryPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Renewal show summary', getFullContextPayload(userId, 'renewal_show_summary', data))

const emitRenewalConfirmEnrollmentPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Renewal confirm enrollment', getFullContextPayload(userId, 'renewal_confirm_enrollment', data))

const emitRenewalCurrYearMedicalPlanDetailsPageViewEvent = (userId: string, data: OnboardingInfo, stepMetadata?: Record<string, any>) =>
  window.segment?.page('[EE] Renewal current year medical plan details', {
    ...getFullContextPayload(userId, 'renewal_curr_year_medical_plan_details', data, stepMetadata),
    plan_id: stepMetadata?.planIds?.[0],
  })

const emitRenewalNextYearMedicalPlanDetailsPageViewEvent = (userId: string, data: OnboardingInfo, stepMetadata?: Record<string, any>) =>
  window.segment?.page('[EE] Renewal next year medical plan details', {
    ...getFullContextPayload(userId, 'renewal_next_year_medical_plan_details', data, stepMetadata),
    plan_id: stepMetadata?.planIds?.[0],
  })

const emitSmartBudgetPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] Smart budget', getFullContextPayload(userId, 'smart_budget', data))

// BYOP events
export const emitByopEligibilityCheckPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] BYOP eligibility check', getFullContextPayload(userId, 'medical_plan', data))

export const emitByopNoPlansStateDirectionsPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] BYOP no plans state directions', getFullContextPayload(userId, 'medical_plan', data))

export const emitByopPlanDetailsFormPageViewEvent = (userId: string, data: OnboardingInfo) =>
  window.segment?.page('[EE] BYOP plan details form', getFullContextPayload(userId, 'medical_plan', data))

export const emitPostEnrollmentPageViewEvent = (
  userId?: string,
  enrollmentData?: {
    employerName?: string;
    coverageStartDate?: string;
    allowanceAmount?: string;
    totalPremiumsAmount?: string;
    enrollmentCount?: number;
    onThatchEnrollmentCount?: number;
    offThatchEnrollmentCount?: number;
    selfPayEnrollmentCount?: number;
    medicareEnrollmentCount?: number;
    qmeTreatment?: string;
    payByBank?: boolean;
    hasLeftOverFunds?: boolean;
    needsPayrollDeduction?: boolean;
  }
) =>
  window.segment?.page('[EE] Post enrollment', {
    referrer_page: document.referrer,
    userId,
    employer_name: enrollmentData?.employerName,
    coverage_start_date: enrollmentData?.coverageStartDate,
    allowance_amount: enrollmentData?.allowanceAmount,
    total_premiums_amount: enrollmentData?.totalPremiumsAmount,
    enrollment_count: enrollmentData?.enrollmentCount,
    on_thatch_enrollment_count: enrollmentData?.onThatchEnrollmentCount,
    off_thatch_enrollment_count: enrollmentData?.offThatchEnrollmentCount,
    self_pay_enrollment_count: enrollmentData?.selfPayEnrollmentCount,
    medicare_enrollment_count: enrollmentData?.medicareEnrollmentCount,
    qme_treatment: enrollmentData?.qmeTreatment,
    pay_by_bank: enrollmentData?.payByBank,
    has_left_over_funds: enrollmentData?.hasLeftOverFunds,
    needs_payroll_deduction: enrollmentData?.needsPayrollDeduction,
  })

export const emitPostEnrollmentPageExitEvent = (durationSeconds: number, userId?: string) =>
  window.segment?.track('[EE] Post enrollment page exit', {
    referrer_page: document.referrer,
    userId,
    time_on_page_s: durationSeconds,
  })

type PageViewStep = Exclude<Step, `medical_plan_details:${string}` | `medical_plan_comparison:${string}` | 'completed'>

const stepEventMapping: Record<PageViewStep, (userId: string, data: OnboardingInfo, stepMetadata?: Record<string, any>) => void> = {
  generate_allowances: (userId, data) => emitGenerateAllowancesPageViewEvent(userId, data),
  phone_verification: (userId, data) => emitPhoneVerificationPageViewEvent(userId, data),
  view_allowances: (userId, data) => emitViewAllowancesPageViewEvent(userId, data),
  additional_details: (userId, data) => emitAdditionalDetailsPageViewEvent(userId, data),
  subsidy_eligible: (userId, data) => emitSubsidyEligiblePageViewEvent(userId, data),
  medical_plan: (userId, data) => emitMedicalPlansPageViewEvent(userId, data),
  medical_plan_details: (userId, data, stepMetadata) => emitMedicalPlanDetailsPageViewEvent(userId, data, stepMetadata),
  medical_plan_comparison: (userId, data, stepMetadata) => emitMedicalPlanComparisonPageViewEvent(userId, data, stepMetadata),
  in_network_verification: (userId, data, stepMetadata) => emitInNetworkVerificationPageViewEvent(userId, data, stepMetadata),
  dental_plan: (userId, data) => emitDentalPlansPageViewEvent(userId, data),
  vision_plan: (userId, data) => emitVisionPlansPageViewEvent(userId, data),
  state_form: (userId, data) => emitStateFormPageViewEvent(userId, data),
  finalize_enrollment: (userId, data) => emitFinalizeEnrollmentPageViewEvent(userId, data),
  opted_out: (userId, data) => emitOptedOutPageViewEvent(userId, data),
  opted_out_from_subsidy_eligible: (userId, data) => emitOptedOutFromSubsidyEligiblePageViewEvent(userId, data),
  renewal_confirm_info: (userId, data) => emitRenewalConfirmInfoPageViewEvent(userId, data),
  renewal_show_summary: (userId, data) => emitRenewalShowSummaryPageViewEvent(userId, data),
  renewal_confirm_enrollment: (userId, data) => emitRenewalConfirmEnrollmentPageViewEvent(userId, data),
  renewal_curr_year_medical_plan_details: (userId, data, stepMetadata) => emitRenewalCurrYearMedicalPlanDetailsPageViewEvent(userId, data, stepMetadata),
  renewal_next_year_medical_plan_details: (userId, data, stepMetadata) => emitRenewalNextYearMedicalPlanDetailsPageViewEvent(userId, data, stepMetadata),
  smart_budget: (userId, data) => emitSmartBudgetPageViewEvent(userId, data),
}

export const emitStepPageViewEvent = (userId: string, step: Step, data: OnboardingInfo, stepMetadata?: Record<string, any>) => {
  const pageViewStep = getBaseStep(step) as PageViewStep
  const eventFunction = stepEventMapping[pageViewStep]

  if (eventFunction) {
    eventFunction(userId, data, stepMetadata)
  } else {
    console.warn(`No page view event defined for step: ${step}`)
  }
}
